import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import {
  ErrorTooltip,
  type Field,
  type Patient,
  Text,
} from '@pbt/pbt-ui-components'

import NotesTemplateInput from '~/components/dashboard/template-inputs/NotesTemplateInput'

import { PetParentConsentValue, PMRConsentValue } from './appointmentEnums'

const useStyles = makeStyles(
  (theme) => ({
    sectionHeading: {
      fontSize: '1.4rem',
    },
    radioGroup: {
      '& .MuiFormControlLabel-root': {
        marginBottom: theme.spacing(-1),
        '&:last-child': {
          marginBottom: 0,
        },
      },
    },
    hasSeenVetRadioGroup: {
      width: '70%',
    },
    petParentConsentRadioGroup: {
      width: '40%',
    },
    sectionContainer: {
      width: 'fit-content',
    },
    errorTooltipWrapper: {
      marginTop: `${theme.spacing(-2)} !important`,
    },
    errorTooltip: {
      width: 'fit-content',
      maxWidth: 'none',
    },
    errorText: {
      color: theme.palette.error.main,
    },
    petParentConsentBox: {
      backgroundColor: theme.colors.shadesNeedsAttention,
      border: `1px solid ${theme.colors.uiNeedsAttention}`,
      borderRadius: 2,
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    petParentConsentText: {
      color: theme.colors.uiNeedsAttention,
    },
    internalNoteSubtitle: {
      color: theme.colors.grayGray2,
    },
  }),
  { name: 'PMRConsentStep' },
)

export interface PMRConsentStepProps {
  fields: {
    hasSeenVeterinarian: Field
    internalNote: Field
    petParentConsent: Field
    previousPracticeSearchValue: Field
  }
  headerPadding: number
  patient?: Patient | null
}

const PMRConsentStep = ({
  fields,
  headerPadding,
  patient,
}: PMRConsentStepProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'TimeTable'])

  const {
    hasSeenVeterinarian,
    previousPracticeSearchValue,
    petParentConsent,
    internalNote,
  } = fields

  const showFindPreviousPractice =
    hasSeenVeterinarian.value === PMRConsentValue.YES

  const showConsentAndNotesSections =
    Boolean(previousPracticeSearchValue.value?.trim()) &&
    showFindPreviousPractice

  return (
    <>
      <Text pb={headerPadding} variant="h2">
        {t('TimeTable:ADD_APPOINTMENT_COMPONENT.NEW_APPOINTMENT')}
      </Text>
      <Grid
        container
        className={classes.sectionContainer}
        direction="column"
        mb={1}
      >
        <Text className={classes.sectionHeading} pb={1} variant="h2">
          {t('TimeTable:PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
        </Text>
        <Text
          className={
            hasSeenVeterinarian.open && !hasSeenVeterinarian.valid
              ? classes.errorText
              : undefined
          }
          pb={0}
          variant="body2"
        >
          {patient?.name
            ? t('TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_QUESTION', {
                patientName: patient.name,
              })
            : t(
                'TimeTable:PMR_CONSENT.HAS_SEEN_VETERINARIAN_QUESTION_FALLBACK',
              )}
        </Text>
        <ErrorTooltip
          classes={{
            errorPopper: classes.errorTooltipWrapper,
            errorTooltip: classes.errorTooltip,
          }}
          message={hasSeenVeterinarian.message}
          open={hasSeenVeterinarian.open && !hasSeenVeterinarian.valid}
          placement="bottom-start"
        >
          <RadioGroup
            aria-label="has-seen-veterinarian"
            className={classNames(
              classes.radioGroup,
              classes.hasSeenVetRadioGroup,
            )}
            name="hasSeenVeterinarian"
            value={hasSeenVeterinarian.value}
            onChange={(e) =>
              hasSeenVeterinarian.setValue(e.target.value as PMRConsentValue)
            }
          >
            <FormControlLabel
              control={<Radio />}
              label={t('Common:YES')}
              value={PMRConsentValue.YES}
            />
            <FormControlLabel
              control={<Radio />}
              label={t('Common:NO')}
              value={PMRConsentValue.NO}
            />
            <FormControlLabel
              control={<Radio />}
              label={t('Common:UNKNOWN')}
              value={PMRConsentValue.UNKNOWN}
            />
          </RadioGroup>
        </ErrorTooltip>
      </Grid>
      {showFindPreviousPractice && (
        <Grid container direction="column" mb={3}>
          <Text className={classes.sectionHeading} pb={1} variant="h2">
            {t('TimeTable:PMR_CONSENT.FIND_PREVIOUS_PRACTICE_TITLE')}
          </Text>
          <Text pb={1} variant="body2">
            {t('TimeTable:PMR_CONSENT.FIND_PREVIOUS_PRACTICE_INSTRUCTIONS')}
          </Text>
          {/* TODO(CVC-29371): Replace this TextField with clinic search component when ready */}
          <ErrorTooltip
            classes={{
              errorTooltip: classes.errorTooltip,
            }}
            message={previousPracticeSearchValue.message}
            open={
              previousPracticeSearchValue.open &&
              !previousPracticeSearchValue.valid
            }
            placement="bottom"
          >
            <TextField
              fullWidth
              placeholder={t(
                'TimeTable:PMR_CONSENT.FIND_PREVIOUS_PRACTICE_PLACEHOLDER',
              )}
              value={previousPracticeSearchValue.value}
              variant="outlined"
              onChange={(e) =>
                previousPracticeSearchValue.setValue(e.target.value)
              }
            />
          </ErrorTooltip>
        </Grid>
      )}
      {showConsentAndNotesSections && (
        <>
          <Grid
            container
            className={classes.petParentConsentBox}
            direction="column"
            mb={2}
          >
            <Text
              className={
                petParentConsent.open && !petParentConsent.valid
                  ? classes.errorText
                  : classes.petParentConsentText
              }
              variant="h4"
            >
              {t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_TITLE')}
            </Text>
            <Text
              className={
                petParentConsent.open && !petParentConsent.valid
                  ? classes.errorText
                  : classes.petParentConsentText
              }
              pb={1}
              variant="body2"
            >
              {t('TimeTable:PMR_CONSENT.PET_PARENT_CONSENT_QUESTION')}
            </Text>
            <ErrorTooltip
              classes={{
                errorPopper: classes.errorTooltipWrapper,
                errorTooltip: classes.errorTooltip,
              }}
              message={petParentConsent.message}
              open={petParentConsent.open && !petParentConsent.valid}
              placement="bottom"
            >
              <RadioGroup
                aria-label="pet-parent-consent"
                className={classNames(
                  classes.radioGroup,
                  classes.petParentConsentRadioGroup,
                )}
                name="petParentConsent"
                value={petParentConsent.value}
                onChange={(e) =>
                  petParentConsent.setValue(
                    e.target.value as PetParentConsentValue,
                  )
                }
              >
                <FormControlLabel
                  control={<Radio />}
                  label={t('Common:YES')}
                  value={PetParentConsentValue.YES}
                />
                <FormControlLabel
                  control={<Radio />}
                  label={t('Common:NO')}
                  value={PetParentConsentValue.NO}
                />
              </RadioGroup>
            </ErrorTooltip>
          </Grid>
          <Grid container direction="column" mb={4}>
            <NotesTemplateInput
              hidePanel
              singleLine
              classes={{
                subTitle: classes.internalNoteSubtitle,
              }}
              subTitle={t('TimeTable:PMR_CONSENT.INTERNAL_NOTE_SUBTITLE')}
              title={t('TimeTable:PMR_CONSENT.INTERNAL_NOTE_TITLE')}
              value={internalNote.value}
              onChange={internalNote.setValue}
            />
          </Grid>
        </>
      )}
    </>
  )
}

export default PMRConsentStep
